import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'

interface TypingEffectProps {
  words: string[]
  className?: string
  typingSpeed?: number
  deleteSpeed?: number
  pauseDuration?: number
}

export default function TypingEffect({
  words,
  className = '',
  typingSpeed = 100,
  deleteSpeed = 50,
  pauseDuration = 2000,
}: TypingEffectProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [currentWord, setCurrentWord] = useState(0)
  const [displayedText, setDisplayedText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!isInView) return

    const word = words[currentWord]
    let timeout: NodeJS.Timeout

    if (!isDeleting) {
      // Typing
      if (displayedText.length < word.length) {
        timeout = setTimeout(() => {
          setDisplayedText(word.slice(0, displayedText.length + 1))
        }, typingSpeed)
      } else {
        // Finished typing, pause then start deleting
        timeout = setTimeout(() => {
          setIsDeleting(true)
        }, pauseDuration)
      }
    } else {
      // Deleting
      if (displayedText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayedText(displayedText.slice(0, -1))
        }, deleteSpeed)
      } else {
        // Finished deleting, move to next word
        setIsDeleting(false)
        setCurrentWord((prev) => (prev + 1) % words.length)
      }
    }

    return () => clearTimeout(timeout)
  }, [displayedText, isDeleting, currentWord, words, isInView, typingSpeed, deleteSpeed, pauseDuration])

  return (
    <span ref={ref} className={className}>
      {displayedText}
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.8, repeat: Infinity }}
        className="inline-block w-0.5 bg-accent ml-1 align-middle"
        style={{ height: '1em' }}
      />
    </span>
  )
}


